import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import DeviceFrame from './DeviceFrame';
import type { ManifestScreenshotData } from './ScreenshotSlide';
import { resolveImageUrl } from './ScreenshotSlideshow';

const ROLES = ['athlete', 'coach', 'guardian'] as const;
type RoleKey = (typeof ROLES)[number];

const ROLE_LABELS: Record<RoleKey, string> = {
    athlete: 'Athlete',
    coach: 'Coach',
    guardian: 'Guardian',
};

interface ProgramStep {
    step_number: number;
    stage_number: number;
    stage: string;
    title: string;
    description: string;
    url: string;
    dom_selector: string;
    screenshots: ManifestScreenshotData[];
}

interface RoleData {
    role: RoleKey;
    steps: ProgramStep[];
}

const ScreenshotTimeline: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [roleData, setRoleData] = useState<RoleData[]>([]);

    useEffect(() => {
        fetch('/json/program-steps.json')
            .then(res => res.json())
            .then((data: RoleData[]) => setRoleData(data))
            .catch(() => setRoleData([]));
    }, []);

    const { sortedStepNumbers, stepsByRoleAndNumber, stepMeta } = useMemo(() => {
        const allStepNumbers = new Set<number>();
        const byRoleAndNumber: Record<string, Record<number, ProgramStep>> = {};
        const meta = new Map<number, { stage: string; title: string; description: string }>();

        for (const rd of roleData) {
            byRoleAndNumber[rd.role] = {};
            for (const step of rd.steps) {
                allStepNumbers.add(step.step_number);
                byRoleAndNumber[rd.role][step.step_number] = step;
                if (!meta.has(step.step_number)) {
                    meta.set(step.step_number, { stage: step.stage, title: step.title, description: step.description });
                }
            }
        }

        return {
            sortedStepNumbers: Array.from(allStepNumbers).sort((a, b) => a - b),
            stepsByRoleAndNumber: byRoleAndNumber,
            stepMeta: meta,
        };
    }, [roleData]);

    if (roleData.length === 0) {
        return (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {ROLES.map(r => (
                    <Skeleton key={r} variant="rounded" width={280} height={400} />
                ))}
            </Box>
        );
    }

    let lastStage = '';

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                borderLeft: 1,
                borderRight: 1,
                borderColor: 'divider',
            }}
        >
            {/* Column headers */}
            {ROLES.map((role, i) => (
                <Box
                    key={role}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        py: 1.5,
                        px: 2,
                        fontWeight: 700,
                        borderLeft: i > 0 ? 1 : 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" component="span" fontWeight={700}>
                        {ROLE_LABELS[role]}
                    </Typography>
                </Box>
            ))}

            {/* Rows */}
            {sortedStepNumbers.map((stepNum) => {
                const meta = stepMeta.get(stepNum);
                const showStageDivider = meta && meta.stage !== lastStage;
                if (meta) lastStage = meta.stage;

                return (
                    <React.Fragment key={stepNum}>
                        {showStageDivider && (
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    py: 1,
                                    px: 2,
                                    bgcolor: 'action.hover',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Chip
                                    label={meta!.stage}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                        )}

                        {ROLES.map((role, i) => {
                            const step = stepsByRoleAndNumber[role]?.[stepNum];
                            const shot = step?.screenshots[0];
                            const imageUrl = shot ? resolveImageUrl(shot, isDark, 'mobile') : null;

                            return (
                                <Box
                                    key={role}
                                    sx={{
                                        borderLeft: i > 0 ? 1 : 0,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {step ? (
                                        <>
                                            {imageUrl ? (
                                                <Box sx={{ margin: 'auto', mt: 1 }}>
                                                    <DeviceFrame device="mobile">
                                                        <Box
                                                            component="img"
                                                            src={imageUrl}
                                                            alt={shot?.title ?? step.title}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </DeviceFrame>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        minHeight: 60,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: 'action.hover',
                                                        flexGrow: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                        {step.title || 'No screenshot'}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ p: 1.5 }}>
                                                {step.title && (
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        {step.title}
                                                    </Typography>
                                                )}
                                                {step.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {step.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default ScreenshotTimeline;
